import { useEffect } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";

import { useUser } from "src/contexts/user-context";
import { useCampaignCalendarConnectorSetupQuery } from "src/graphql";
import { getStoredValue, setStoredValue } from "src/hooks/use-persisted-state";
import { useLocation } from "src/router";

import {
  type CalendarSendsState,
  deriveCalendarSendsState,
} from "src/pages/agents/calendar/setup/calendar-setup-status";
import { useConnectorSetupRefetchInterval } from "src/pages/agents/calendar/setup/use-calendar-setup";

export const CALENDAR_PATH = "/agents/calendar";
/** The first import takes days, so the nav can check less often than the calendar page. */
const NAV_MAX_POLL_MS = 15 * 60_000;

export const calendarAwaitingReadyKey = (workspaceId: string | number) =>
  `${workspaceId}.agents.calendarAwaitingReady`;

/**
 * Visiting before the calendar is ready asks to be told when it is; visiting
 * once it is ready answers that, so a cleared notice never comes back.
 */
export function useRecordCalendarVisit(sendsState: CalendarSendsState) {
  const { workspace } = useUser();
  const workspaceId = workspace?.id;
  useEffect(() => {
    if (workspaceId === undefined) return;
    setStoredValue(
      calendarAwaitingReadyKey(workspaceId),
      sendsState !== "ready",
    );
  }, [workspaceId, sendsState]);
}

/** Whether the nav should flag a calendar that became ready since the last visit. */
export function useCalendarReadyNotice(): boolean {
  const { campaignCalendar } = useFlags();
  const { workspace } = useUser();
  const { pathname } = useLocation();
  const isOnCalendar = pathname.replace(/\/+$/, "").endsWith(CALENDAR_PATH);
  const isAwaiting =
    Boolean(campaignCalendar) &&
    workspace !== undefined &&
    getStoredValue(calendarAwaitingReadyKey(workspace.id), false);
  const refetchInterval = useConnectorSetupRefetchInterval({
    maxMs: NAV_MAX_POLL_MS,
    isSettled: (connectors) => deriveCalendarSendsState(connectors) === "ready",
  });
  // Only users who saw the calendar before it was ready get notified, so only they poll.
  const { data } = useCampaignCalendarConnectorSetupQuery(undefined, {
    enabled: isAwaiting && !isOnCalendar,
    refetchInterval,
  });
  return (
    isAwaiting &&
    !isOnCalendar &&
    data !== undefined &&
    deriveCalendarSendsState(data.campaignCalendarConnectorSetup) === "ready"
  );
}
